import React from 'react';

import styled from '@emotion/styled';

import { useQuery } from '@apollo/client/react';

import { FontWeight, Text } from '@clutter/clean';

import {
  GeocodeDocument,
  OrderCancellationPolicyGroupDocument,
} from '@graphql/platform';

import { WT_VISITOR_TOKEN } from '@root/initializers/wt';

import { useSharedCheckoutContext } from '../context';
import {
  CheckoutModal,
  ModalBodyParagraph,
  ModalCloseButton,
  ModalContainer,
  ModalCopySection,
  ModalCopyStack,
  ModalScrollableBody,
  ModalSectionHeading,
  ModalTitle,
} from '../steps/subcomponents/modal_shared';
import { PolicyModal } from './subcomponents/policy_modal';

const Body = styled(Text.Body)`
  padding-top: 16px;
  padding-bottom: 40px;
`;

export type PolicyLearnMoreModalVariant = 'control' | 'challenger';

/** Cancellation policy: control = teal PolicyModal; challenger = white funnel modal (Figma). */
const OnboardingCancellationPolicyModal: React.FC<{
  isOpen: boolean;
  handleModalClose(): void;
  zip?: string | null;
  dateScheduled?: { fromTime: string } | null;
  variant?: PolicyLearnMoreModalVariant;
}> = ({
  isOpen,
  handleModalClose,
  zip,
  dateScheduled,
  variant = 'control',
}) => {
  const geocode =
    useQuery(GeocodeDocument, {
      variables: { zip: zip! },
      skip: !zip,
    }).data?.geocode ?? undefined;
  const { data } = useQuery(OrderCancellationPolicyGroupDocument, {
    variables: {
      visitorToken: WT_VISITOR_TOKEN,
      orderScheduledTime: dateScheduled?.fromTime,
    },
    skip: !geocode || geocode?.region?.name === 'Los Angeles',
  });
  const cancellationPolicy =
    data?.orderCancellationPolicyGroup?.onboardingCancellationPolicy;

  if (variant === 'challenger') {
    return (
      <CheckoutModal isOpen={isOpen} onClose={handleModalClose}>
        <ModalContainer>
          <ModalCloseButton onClose={handleModalClose} />
          <ModalScrollableBody>
            <ModalCopyStack>
              <ModalCopySection>
                <ModalTitle>When will I be charged?</ModalTitle>
                <ModalBodyParagraph>
                  Don’t worry, your reservation is completely free! Your card
                  will not be charged until your appointment is complete.
                </ModalBodyParagraph>
              </ModalCopySection>
              <ModalCopySection>
                <ModalSectionHeading>
                  What if I need to reschedule or cancel?
                </ModalSectionHeading>
                <ModalBodyParagraph>
                  {cancellationPolicy ? (
                    cancellationPolicy.paragraph
                  ) : (
                    <>
                      We ask that you reschedule or cancel at least 48hrs before
                      your appointment. Otherwise, you will be charged a $100
                      charge. This charge is increased to $200 on the same day
                      of the appointment. You can reschedule or cancel online in
                      just a few clicks.
                    </>
                  )}
                </ModalBodyParagraph>
              </ModalCopySection>
              <ModalCopySection>
                <ModalSectionHeading>
                  What if I cancel before my term is over?
                </ModalSectionHeading>
                <ModalBodyParagraph>
                  You may cancel your Subscription Services at any time once the
                  minimum storage term has been met.
                </ModalBodyParagraph>
              </ModalCopySection>
            </ModalCopyStack>
          </ModalScrollableBody>
        </ModalContainer>
      </CheckoutModal>
    );
  }

  return (
    <PolicyModal isOpen={isOpen} handleModalClose={handleModalClose}>
      <Text.Headline weight={FontWeight.Medium}>
        When will I be charged?
      </Text.Headline>
      <Body>
        Don’t worry, your reservation is completely free! Your card will not be
        charged until your appointment is complete.
      </Body>
      <Text.Headline weight={FontWeight.Medium}>
        What if I need to reschedule or cancel?
      </Text.Headline>
      <Body>
        {cancellationPolicy ? (
          cancellationPolicy.paragraph
        ) : (
          <>
            We ask that you reschedule or cancel at least 48hrs before your
            appointment. Otherwise, you will be charged a $100 charge. This
            charge is increased to $200 on the same day of the appointment. You
            can reschedule or cancel online in just a few clicks.
          </>
        )}
      </Body>
      <Text.Headline weight={FontWeight.Medium}>
        What if I cancel before my term is over?
      </Text.Headline>
      <Body>
        You may cancel your Subscription Services at any time once the minimum
        storage term has been met.
      </Body>
    </PolicyModal>
  );
};

export const StorageCancellationModal: React.FC<{
  isOpen: boolean;
  handleModalClose(): void;
}> = ({ isOpen, handleModalClose }) => {
  const {
    flowState: { values },
  } = useSharedCheckoutContext();
  return (
    <OnboardingCancellationPolicyModal
      isOpen={isOpen}
      handleModalClose={handleModalClose}
      zip={values.zip}
      dateScheduled={values.dateScheduled}
    />
  );
};
